// The site's topology, decided (#297, ZENITH.md §11.3, ADR-0045 decision 5):
// its declared links beside what each of its switches observes, compared port
// by port and never merged. Everything here is a pure function of two reads —
// the site's links (`GET /sites/{id}/links`, intent) and each switch's live
// port state (`GET /switches/{serial}/ports/state`, observation) — so the
// verdicts are tested as values and the drawing in `Topology.tsx` only places
// what this decided.
//
// **Observation never becomes intent.** Nothing here produces a link, and
// nothing here removes one; a neighbour no link declares is *shown* as such
// and stays what it is. The only way an observation becomes a link is the
// association gesture on the device page (`Links.tsx`), an operator's act.
//
// **A verdict is per switch end.** A link has two ends; the switch at one of
// them is what reports, and a link between two switches is read twice. The
// line the drawing paints takes the two readings together (`worst`), and the
// table says each in words.
//
// **A planned end is read through its binding** (#520, ADR-0045 decision 1,
// ADR-0068 decision 3). An end that names a planned device carries the
// device's bound serial when an installer has bound one — `planned_device.
// serial`, resolved by the server on every read — and every comparison here
// asks an end for its *effective* serial (`serialOf`): a claimed end's own, a
// bound planned end's through the device, and none for a planned end still
// waiting for hardware. So a link to a planned device is unobserved until
// the binding, and the same link — no row rewritten — is judged like any
// other once the serial arrives.
//
// **A link's word has a verdict of its own, and it is never merged either**
// (#588, ADR-0090 decision 3). A link may say `uplink` — end A's device
// reaches the site's way out through end B — or `stack`. The word is
// compared with the port the switch at end A reports it reaches its cell
// through (`wan`, the reading ADR-0088's mark derives from): the same port
// confirms it, another port contradicts it with that port named, and a
// refused or absent reading leaves it unobserved with the reason. A `stack`
// is always unobserved, said as such, because no read of Zenith's confirms
// one. The word's verdict sits beside the link's, which is a separate
// thing: a cable can be where it was declared and still not be the switch's
// way out. Nothing here reads the word to lay anything out (decision 4).
//
// **The tree is read from the uplinks, and from nothing declared** (#586,
// ADR-0079, ADR-0088). Each switch's live read marks the one port it reaches
// its cell through (`wan`); the neighbour it reports on that port, when it is
// a switch of the site, is the switch above it, and anything else there —
// the customer's router, nothing at all — makes it a root, unless another
// switch's forwarding table learned it on a port that is not that switch's
// own uplink, which places it there instead, learned and not announced
// (`uplinksOf`, ADR-0448). A read that
// refused the uplink, or was not made, places the switch nowhere: it is
// named under *uplink not read* rather than guessed into a row. A declared
// link between two switches is a line with a verdict, as every link is, and
// moves no switch — declared and observed stay two things here too.
import type { components } from "../../api/schema";
import { byNumber } from "./DeviceFace";
import { isSerialName } from "./pieces";
import { neighbourWords, reasonWords } from "./Ports";

export type Link = components["schemas"]["Link"];
export type LinkEnd = components["schemas"]["LinkEnd"];
export type LiveSet = components["schemas"]["SwitchPortStateSet"];
export type LivePort = components["schemas"]["SwitchPortState"];
export type Neighbour = components["schemas"]["SwitchPortNeighbour"];
export type Device = components["schemas"]["Device"];
export type WayOut = components["schemas"]["WayOut"];

/** The three answers a switch gives about a declared link (decision 5). */
export type Verdict = "confirmed" | "contradicted" | "unobserved";

/** What one switch's live read came to, for the comparison: its answer, or
 * why there is none — still out, or failed. */
export type Observation = { kind: "ready"; set: LiveSet } | { kind: "unread"; why: string };

/** One switch end's reading of a link: the verdict and, in words, what the
 * switch reported that led to it. */
export interface Reading { serial: string; port: string; verdict: Verdict; why: string }

/** The word's own verdict (ADR-0090 decision 3), in words. */
export interface RoleReading { verdict: Verdict; why: string }

/** A link with both ends read (a non-switch end reads as nothing), the
 * line's verdict from the two, and the word's own verdict when the link
 * carries one. */
export interface Judged { link: Link; readings: Reading[]; verdict: Verdict; unconfirmed?: string; role?: RoleReading }

/** A neighbour a switch reports on a port no link declares: observed, and
 * undeclared. Shown, never made into anything (decision 4). */
export interface Undeclared { serial: string; port: string; neighbour: Neighbour }

/** The serial an end is judged by: a claimed end's own, a planned end's
 * through its bound device, and undefined for a planned end no installer
 * has bound — which has no MAC a switch could report (decision 5). */
export function serialOf(e: LinkEnd): string | undefined {
  if (e.kind === "planned") return e.planned_device?.serial || undefined;
  return e.serial || undefined;
}

/** Whether an end names a planned device that is still waiting for its
 * hardware: the one case a link is drawn unconfirmed by construction. */
export const isUnbound = (e: LinkEnd): boolean => e.kind === "planned" && !e.planned_device?.serial;

/** An end with the serial it is judged by, as `readEnd` takes it. */
export interface Witness { serial: string; port: string }

/** The ports of `set` on which the device `serial` is the reported neighbour. */
export function neighbourPorts(set: LiveSet, serial: string): string[] {
  return set.ports.filter((p) => p.neighbour?.in_site && p.neighbour.serial === serial).map((p) => p.port);
}

/** What the switch at `end` says about the link to `other` (decision 5).
 *
 * Confirmed: the switch reports the other end's device on that port.
 * Contradicted: it reports a different device there, or that device on a
 * different port. Unobserved: it did not answer, the port carries no
 * neighbour, or the other end is planned and so has no MAC to match. The
 * remote port the neighbour names for itself (`eth0`) is said, never compared
 * with the declared port name: the two are different vocabularies and nothing
 * translates between them (decision 3). */
export function readEnd(end: Witness, other: LinkEnd, obs: Observation | undefined, name: (serial: string) => string): Reading {
  const at = { serial: end.serial, port: end.port };
  const otherSerial = serialOf(other);
  if (!otherSerial) return { ...at, verdict: "unobserved", why: "the other end is planned and has no MAC to match until the hardware arrives" };
  if (!obs) return { ...at, verdict: "unobserved", why: "no switch of the site observes this end" };
  if (obs.kind === "unread") return { ...at, verdict: "unobserved", why: obs.why };
  const set = obs.set;
  if (set.unanswered) return { ...at, verdict: "unobserved", why: reasonWords(set.unanswered.reason) };
  const port = set.ports.find((p) => p.port === end.port);
  if (!port) return { ...at, verdict: "unobserved", why: `the switch's configuration declares no port ${end.port}` };
  const u = port.unanswered.find((x) => x.field === "neighbour");
  if (u) return { ...at, verdict: "unobserved", why: `neighbor unanswered: ${reasonWords(u.reason, u.field)}` };
  const elsewhere = neighbourPorts(set, otherSerial).filter((p) => p !== end.port);
  const there = elsewhere.length ? `, and ${name(otherSerial)} on ${elsewhere.join(", ")}` : "";
  const n = port.neighbour;
  if (n && n.in_site && n.serial === otherSerial) {
    return { ...at, verdict: "confirmed", why: `the switch reports ${neighbourWords(n)} on ${end.port}` };
  }
  if (n) return { ...at, verdict: "contradicted", why: `the switch reports ${neighbourWords(n)} on ${end.port}${there}` };
  if (elsewhere.length) return { ...at, verdict: "contradicted", why: `the switch reports no neighbor on ${end.port}${there}` };
  return { ...at, verdict: "unobserved", why: `the switch reports no neighbor on ${end.port}` };
}

/** Two readings into one line: a contradiction anywhere is the finding, a
 * confirmation from either switch is enough, and silence from both is
 * silence. */
export function worst(readings: Reading[]): Verdict {
  if (readings.some((r) => r.verdict === "contradicted")) return "contradicted";
  if (readings.some((r) => r.verdict === "confirmed")) return "confirmed";
  return "unobserved";
}

/** What the switch at end A says about a declared `uplink` (ADR-0090
 * decision 3): confirmed when the port it reports it reaches its cell
 * through is the declared one, contradicted with the port it named
 * otherwise, and unobserved — with the reason — when end A is planned, is
 * not a switch of the site, was not read, or the reading refused (ADR-0088
 * decision 4's three). A `stack` has no reading to be compared with and is
 * unobserved always, said as such. */
export function readRole(link: Link, observed: Map<string, Observation>, name: (serial: string) => string): RoleReading | undefined {
  if (!link.role) return undefined;
  // Restates ADR-0090 decision 3 — a stack has no reading to be compared with, so it is unobserved
  // always, said as that and not as an absence.
  if (link.role === "stack") return { verdict: "unobserved", why: "nothing read here can confirm a stack; the role is what the operator declared" };
  const a = link.a;
  const serial = serialOf(a);
  if (!serial) return { verdict: "unobserved", why: "end A is planned and reports nothing until the hardware arrives" };
  const obs = observed.get(serial);
  if (!obs) return { verdict: "unobserved", why: `${name(serial)} is not a switch of the site, so nothing reports its uplink` };
  if (obs.kind === "unread") return { verdict: "unobserved", why: obs.why };
  const set = obs.set;
  if (set.unanswered) return { verdict: "unobserved", why: reasonWords(set.unanswered.reason) };
  const wan = set.ports.find((p) => p.wan);
  if (wan && wan.port === a.port) return { verdict: "confirmed", why: `${name(serial)} reports ${a.port} as the port it reaches its controller through` };
  if (wan) return { verdict: "contradicted", why: `${name(serial)} reports ${wan.port}, not ${a.port}, as the port it reaches its controller through` };
  const refused = set.ports.flatMap((p) => p.unanswered).find((u) => u.field === "wan");
  if (refused) return { verdict: "unobserved", why: reasonWords(refused.reason, "wan") };
  return { verdict: "unobserved", why: `the reading for ${name(serial)} names no port as the one it reaches its controller through` };
}

/** What a link is, read from one of its ends (#883). */
export type EndWord = "uplink" | "downlink" | "stack";

/** The link's word as the device at `end` reads it (ADR-0090 decision 2b):
 * an `uplink` is end A's way out, so it reads `uplink` at A and `downlink`
 * at B; a `stack` reads `stack` at both; a link with no word reads nothing.
 * `downlink` is drawn here and never stored or sent — it is the same cable
 * read from the other end, and not a word of the row. */
export function wordAt(link: Pick<Link, "role">, end: "a" | "b"): EndWord | undefined {
  if (link.role === "stack") return "stack";
  if (link.role === "uplink") return end === "a" ? "uplink" : "downlink";
  return undefined;
}

/** Every link judged against the observations at hand. `observed` holds an
 * entry per switch of the site — absent for a device that is not one. A
 * forwarding table's attribution (`uplinksOf`, ADR-0448) is no input here: it
 * places a switch in the tree and never moves a link's verdict. */
export function judge(links: Link[], observed: Map<string, Observation>, name: (serial: string) => string): Judged[] {
  return links.map((link) => {
    const readings: Reading[] = [];
    for (const [end, other] of [[link.a, link.b], [link.b, link.a]] as const) {
      const serial = serialOf(end);
      if (serial && observed.has(serial)) readings.push(readEnd({ serial, port: end.port }, other, observed.get(serial), name));
    }
    const waiting = [link.a, link.b].some(isUnbound);
    const unconfirmed = waiting ? "a planned end: drawn unconfirmed until the hardware arrives" : readings.length === 0 ? "neither end is a switch of the site, so nothing observes it" : undefined;
    return { link, readings, verdict: worst(readings), unconfirmed, role: readRole(link, observed, name) };
  });
}

/** The speed a switch end negotiated, from the live read and nothing
 * stored (§11.3 `speed_mbps`), in words: `1000 Mb/s`, `down`, or why it
 * is unread. Undefined for an end no switch of the site reports — an
 * access point's or a planned one's. */
export function speedWords(end: LinkEnd, observed: Map<string, Observation>): string | undefined {
  const serial = serialOf(end);
  if (!serial || !observed.has(serial)) return undefined;
  const obs = observed.get(serial)!;
  if (obs.kind === "unread") return `unread: ${obs.why}`;
  if (obs.set.unanswered) return `unread: ${reasonWords(obs.set.unanswered.reason)}`;
  const port = obs.set.ports.find((p) => p.port === end.port);
  if (!port) return `unread: the switch's configuration declares no port ${end.port}`;
  const u = port.unanswered.find((x) => x.field === "link");
  if (u) return `unread: ${reasonWords(u.reason, u.field)}`;
  if (port.link === "down") return "down";
  if (port.speed_mbps !== undefined) return `${port.speed_mbps} Mb/s`;
  if (port.link === "up") return "up, speed unread";
  return "unread: the report says nothing about the link";
}

/** The neighbours each switch reports that no link declares: on a port no
 * link names, and not a device a link at this switch names on another port —
 * that one is the contradiction the link carries, said there and not
 * twice. A port a link does declare is judged above, whatever is on it. */
export function undeclared(links: Link[], observed: Map<string, Observation>): Undeclared[] {
  const ports = new Set<string>();
  const devices = new Set<string>();
  for (const l of links) {
    for (const [e, other] of [[l.a, l.b], [l.b, l.a]] as const) {
      const serial = serialOf(e), otherSerial = serialOf(other);
      if (!serial) continue;
      ports.add(`${serial}/${e.port}`);
      if (otherSerial) devices.add(`${serial}/${otherSerial}`);
    }
  }
  const out: Undeclared[] = [];
  for (const [serial, obs] of observed) {
    if (obs.kind !== "ready" || obs.set.unanswered) continue;
    for (const p of obs.set.ports) {
      const n = p.neighbour;
      if (!n || ports.has(`${serial}/${p.port}`)) continue;
      if (n.in_site && n.serial && devices.has(`${serial}/${n.serial}`)) continue;
      out.push({ serial, port: p.port, neighbour: n });
    }
  }
  return out;
}

/** The site's switches in the order their reads are asked for: `first`
 * ahead of the rest, then by name and serial, so the same site asks in the
 * same order however it was listed. Every switch is read (#1786); the order
 * decides only which reads take the first of the page's `state_fan_out` slots
 * (`fanOut.ts`), and nothing here holds a number of its own. */
export function readOrder(switches: Device[], first: ReadonlySet<string> = new Set()): Device[] {
  return [...switches].sort((a, b) => Number(first.has(b.serial)) - Number(first.has(a.serial)) || a.name.localeCompare(b.name) || a.serial.localeCompare(b.serial));
}

/** The ports a switch's faceplate draws in the topology: the ones its own
 * configuration declares, in the document's order, when the live read
 * answered them, and every port a link names at it after those — the
 * absence rule at this scale, so a declared end is never dropped for want of
 * a document. Without an answer, the ports the links name alone. */
export function portsAt(serial: string, links: Link[], obs: Observation | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  if (obs?.kind === "ready") for (const p of obs.set.ports) if (!seen.has(p.port)) { seen.add(p.port); out.push(p.port); }
  const named: string[] = [];
  for (const l of links) for (const e of [l.a, l.b]) if (serialOf(e) === serial && !seen.has(e.port)) { seen.add(e.port); named.push(e.port); }
  return [...out, ...named.sort(byNumber)];
}

/** The other end of a link from `serial`'s side — the device's own end, or
 * the planned end bound to it — or undefined when the link does not touch
 * it. */
export function otherEnd(link: Link, serial: string): { mine: LinkEnd; other: LinkEnd } | undefined {
  if (serialOf(link.a) === serial) return { mine: link.a, other: link.b };
  if (serialOf(link.b) === serial) return { mine: link.b, other: link.a };
  return undefined;
}

/** The other end of a link from a planned device's side, by its row, or
 * undefined when the link does not name it. */
export function otherEndOfPlanned(link: Link, plannedId: string): { mine: LinkEnd; other: LinkEnd } | undefined {
  if (link.a.kind === "planned" && link.a.planned_device?.id === plannedId) return { mine: link.a, other: link.b };
  if (link.b.kind === "planned" && link.b.planned_device?.id === plannedId) return { mine: link.b, other: link.a };
  return undefined;
}

/** A planned device bound to a serial in words: the piece's one name (#1906,
 * `pieces.tsx`), which is the plan's name where it is a name and the bound
 * device's where the plan's is just the serial. */
export function boundName(pd: { name: string; serial?: string }, name: (serial: string) => string): string {
  return isSerialName(pd.name, pd.serial) ? name(pd.serial!) : pd.name;
}

/** One end in words: the device's name, or its serial when the site's list
 * no longer has it, and the port — as `portOf` says it, which the topology
 * gives as the printed number (`physicalPort`, #1480). A planned end is its planned name, marked
 * planned until an installer binds it; once bound it is that piece's one
 * name (`boundName`), the way the drawing titles it. */
export function endWords(e: LinkEnd, name: (serial: string) => string, portOf: (port: string) => string = (p) => p): string {
  const port = portOf(e.port);
  if (e.kind === "planned") {
    const pd = e.planned_device;
    if (!pd) return `a planned device, ${port}`;
    if (!pd.serial) return `${pd.name} (planned) ${port}`;
    return `${boundName(pd, name)} ${port}`;
  }
  if (!e.serial) return `a ${e.kind} device, ${port}`;
  return `${name(e.serial)} ${port}`;
}

/** The ports of `serial` that links declare, each with the other end in
 * words, for the faceplate's cabled layer. */
export function cabledOf(links: Link[], serial: string, name: (serial: string) => string, portOf?: (port: string) => string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const l of links) {
    const ends = otherEnd(l, serial);
    if (ends) out[ends.mine.port] = endWords(ends.other, name, portOf);
  }
  return out;
}

/** Where a switch's uplink leads, read from its own report and nothing else
 * (ADR-0079, ADR-0088): the switch of the site it reports on its `wan` port
 * (`parent`), a `wan` port carrying no switch of the site — the router, an
 * unknown device, nothing (`root`) — or no answer (`unread`), with
 * the words for why: still out, failed, or the reading
 * refused (`unreported`, `disagreed`, `unresolved`; ADR-0088 decision 4). A
 * `root` may carry a note, which the path on a device's page uses to say
 * where its walk up stopped (#586). A `parent` found from another switch's
 * forwarding table rather than from this switch's own `wan` neighbour carries
 * `learned`, with the parent's port it was learned on (`uplinksOf`,
 * ADR-0448): learned, not announced. */
export type Uplink =
  | { kind: "parent"; port: string; serial: string; learned?: { at: string } }
  | { kind: "root"; port?: string; note?: string }
  | { kind: "unread"; why: string };

/** A switch's uplink from its observation. `isSwitch` says whether a serial
 * is a switch of the site — the neighbour a switch reports carries no
 * category, and the site's list is what says which of its devices are
 * switches; an access point on the way out is not a parent (ADR-0045
 * decision 6) and the switch reporting it is a root. */
export function uplinkOf(obs: Observation | undefined, isSwitch: (serial: string) => boolean): Uplink {
  if (!obs) return { kind: "unread", why: "the switch was not read" };
  if (obs.kind === "unread") return { kind: "unread", why: obs.why };
  const set = obs.set;
  if (set.unanswered) return { kind: "unread", why: reasonWords(set.unanswered.reason) };
  const wan = set.ports.find((p) => p.wan);
  if (wan) {
    const n = wan.neighbour;
    if (n?.in_site && n.serial && isSwitch(n.serial)) return { kind: "parent", port: wan.port, serial: n.serial };
    return { kind: "root", port: wan.port };
  }
  const refused = set.ports.flatMap((p) => p.unanswered).find((u) => u.field === "wan");
  if (refused) return { kind: "unread", why: reasonWords(refused.reason, "wan") };
  return { kind: "unread", why: "the reading names no port as the one the switch reaches its controller through" };
}

/** The ports of `set` that are not one of the switch's own `wan` ports and
 * whose forwarding table lists `serial` (`learned_switches`, ADR-0448). */
function learnedOn(set: LiveSet, serial: string): string[] {
  return set.ports.filter((p) => !p.wan && (p.learned_switches ?? []).includes(serial)).map((p) => p.port);
}

/** Every switch's uplink, site-wide: each switch's own (`uplinkOf`), and then,
 * for a switch that is a root on a `wan` port carrying no switch of the site,
 * the switch whose forwarding table learned it on a port that is not that
 * switch's own `wan` port (ADR-0448, amending ADR-0150 decisions 1a and 5b).
 *
 * **Why the table can say this and the switch's own mark cannot.** A switch's
 * table lists every address that reaches it through a port. A switch below it
 * reaches its controller through it, so its address is listed on the port
 * towards it, and a switch beside it or above it is listed on its own `wan`
 * port, which is why that port is never read here. Measured on Hotel Cicada's
 * ECS-4125-10P with the ECS5550-54X below it and LLDP off on the 5550: the
 * 5550's serial on `Ethernet9` in 15 of 15 reports, and the 4125's uplink is
 * `Ethernet8` (docs/evidence/2026-10-21-with-the-ecs5550-54xs-lldp-off-…).
 *
 * **Every switch above a switch learns it, so the parent is the deepest
 * learner**: the one candidate that every other candidate learned on the same
 * port it learned this switch on. Two candidates neither of which learned the
 * other leave the switch a root, as before, and nothing is guessed. The parent
 * is learned on a port of its own; which port of this switch the cable lands
 * on is this switch's own `wan` mark, and nothing else says it.
 *
 * Only the `root` with a port moves: a switch whose own read placed it under a
 * parent, or did not answer, keeps what its own read said. */
export function uplinksOf(switches: Device[], observed: Map<string, Observation>): Map<string, Uplink> {
  const isSwitch = (serial: string) => switches.some((d) => d.serial === serial);
  const own = new Map(switches.map((d) => [d.serial, uplinkOf(observed.get(d.serial), isSwitch)] as const));
  const ready = (serial: string): LiveSet | undefined => {
    const obs = observed.get(serial);
    return obs?.kind === "ready" && !obs.set.unanswered ? obs.set : undefined;
  };
  const out = new Map(own);
  for (const d of switches) {
    const u = own.get(d.serial)!;
    if (u.kind !== "root" || !u.port) continue;
    // Each other switch that learned this one off its own `wan` port, and on
    // which port: one port per switch, since a switch is reached through one.
    const candidates: { serial: string; at: string }[] = [];
    for (const other of switches) {
      if (other.serial === d.serial) continue;
      const set = ready(other.serial);
      if (!set || !set.ports.some((p) => p.wan)) continue;
      const at = learnedOn(set, d.serial);
      if (at.length === 1) candidates.push({ serial: other.serial, at: at[0]! });
    }
    const deepest = candidates.filter((c) => candidates.every((z) => z === c || learnedOn(ready(z.serial)!, c.serial).includes(z.at)));
    if (deepest.length !== 1) continue;
    out.set(d.serial, { kind: "parent", port: u.port, serial: deepest[0]!.serial, learned: { at: deepest[0]!.at } });
  }
  return out;
}

/** The switches in rows by their distance from the WAN, and the ones no row
 * can hold. Roots first, by name; under each row the switches whose uplink
 * reports a switch of that row, grouped under their parent in the parent's
 * order and by name within a group — so the same reads draw the same rows
 * twice. A switch whose uplink is `unread`, whose parent is not placed, or
 * whose uplink leads back to itself through other switches (two reads that
 * name each other, which no cabling produces and a bad read can) is in
 * `last`, with the words for why, and is never guessed into a row. */
export function treeOf(switches: Device[], uplinks: Map<string, Uplink>, name: (serial: string) => string): { rows: Device[][]; last: { device: Device; why: string }[] } {
  const byName = (a: Device, b: Device) => a.name.localeCompare(b.name) || a.serial.localeCompare(b.serial);
  const ordered = [...switches].sort(byName);
  const parentOf = new Map<string, string>();
  const unread = new Map<string, string>();
  for (const d of ordered) {
    const u = uplinks.get(d.serial) ?? { kind: "unread", why: "the switch was not read" };
    if (u.kind === "parent" && ordered.some((o) => o.serial === u.serial)) parentOf.set(d.serial, u.serial);
    else if (u.kind === "parent") unread.set(d.serial, `uplink leads to ${name(u.serial)}, which is not a switch of this drawing`);
    else if (u.kind === "unread") unread.set(d.serial, u.why);
  }
  const rows: Device[][] = [];
  const placed = new Set<string>();
  let row = ordered.filter((d) => !parentOf.has(d.serial) && !unread.has(d.serial));
  while (row.length > 0) {
    rows.push(row);
    for (const d of row) placed.add(d.serial);
    const next: Device[] = [];
    for (const p of row) next.push(...ordered.filter((d) => parentOf.get(d.serial) === p.serial));
    row = next;
  }
  const last: { device: Device; why: string }[] = [];
  for (const d of ordered) {
    if (placed.has(d.serial)) continue;
    const why = unread.get(d.serial);
    if (why) { last.push({ device: d, why }); continue; }
    // A parent the rows do not hold: unplaced itself, or a loop.
    const seen = new Set<string>([d.serial]);
    let at = parentOf.get(d.serial);
    while (at && !seen.has(at) && !placed.has(at) && parentOf.has(at) && !unread.has(at)) { seen.add(at); at = parentOf.get(at); }
    const parent = parentOf.get(d.serial)!;
    last.push({ device: d, why: at && seen.has(at) ? `uplink leads to ${name(parent)} and back to itself, which no cabling produces` : `uplink leads to ${name(parent)}, which is not placed` });
  }
  return { rows, last };
}

// --- the site's way out (ADR-0150) ---------------------------------------------

/** One port the site leaves by: the `wan` port of a switch the tree places as
 * a root (ADR-0150 decision 1a). A switch's `wan` mark is its own uplink,
 * whatever is on the other end, so a cascaded switch's `wan` port faces its
 * parent and is never one of these (the Context of ADR-0150). */
export interface WayOutPort { serial: string; port: string }

/** The ports the site leaves by, from the tree and nothing else: every root
 * of the first row whose uplink names a port, in the row's order. A root
 * with no port — the top of a device page's walk that stopped short, which
 * carries a note instead — is not one, and neither is any switch in the
 * tree's last row: the node is never drawn on a guess. */
export function waysOutOf(tree: ReturnType<typeof treeOf>, uplinks: Map<string, Uplink>): WayOutPort[] {
  const out: WayOutPort[] = [];
  for (const d of tree.rows[0] ?? []) {
    const u = uplinks.get(d.serial);
    if (u?.kind === "root" && u.port) out.push({ serial: d.serial, port: u.port });
  }
  return out;
}

/** Why the drawing has no way-out node, in words, when it has switches and
 * none of them is a root with a `wan` port — each switch the tree could not
 * place, with its reason, which is ADR-0088 decision 4's refusal where the
 * reading refused (`unreported`, `disagreed`, `unresolved`) and the read's
 * own words where the switch was not read. Undefined when the drawing has a
 * way out, or no switch to have one. */
export function wayOutAbsence(tree: ReturnType<typeof treeOf>, uplinks: Map<string, Uplink>, name: (serial: string) => string): string | undefined {
  if (waysOutOf(tree, uplinks).length > 0) return undefined;
  if (tree.last.length === 0) return undefined;
  const why = tree.last.map((l) => `${name(l.device.serial)}: ${l.why}`).join("; ");
  // Restates ADR-0150 decision 1a, ADR-0088 decision 4 — the WAN node is drawn only from a root's own
  // `wan` mark, and each switch that has none says why. The guide's "which port connects to the
  // internet" is not taken (#1678): the mark is the port a switch reaches its controller through,
  // and nothing here reads the internet. "The site's WAN port" is ADR-0336's wording.
  return `No WAN is drawn: no switch has reported the site's WAN port. ${why}.`;
}

/** A declared way out judged against the mark, beside it and never merged
 * (ADR-0150 decision 3). `why` says what the reads say, in the same shape a
 * link's reading does; `short` is the reason in a few words for the row
 * beside the drawing (#1493's follow-up), absent where the verdict is
 * `confirmed` and needs none. */
export interface WayOutVerdict { verdict: Verdict; why: string; short?: string }

export function judgeWayOut(w: Pick<WayOut, "serial" | "port">, tree: ReturnType<typeof treeOf>, uplinks: Map<string, Uplink>, name: (serial: string) => string): WayOutVerdict {
  if (!w.serial || !w.port) return { verdict: "unobserved", why: "no port declared", short: "no port declared" };
  const who = name(w.serial);
  const last = tree.last.find((l) => l.device.serial === w.serial);
  if (last) return { verdict: "unobserved", why: `${who}'s uplink is not read: ${last.why}`, short: "uplink not read" };
  const u = uplinks.get(w.serial);
  if (!u) return { verdict: "unobserved", why: `${who} is not a switch this drawing read`, short: "switch not read" };
  if (u.kind === "unread") return { verdict: "unobserved", why: `${who}'s uplink is not read: ${u.why}`, short: "uplink not read" };
  if (u.kind === "parent") {
    return u.port === w.port
      ? { verdict: "contradicted", why: `${who} reaches its controller through ${w.port}, but to ${name(u.serial)}: it sits under that switch, so ${w.port} is not the site's WAN port`, short: `under ${name(u.serial)}` }
      : { verdict: "contradicted", why: `${who} reaches its controller through ${u.port}, under ${name(u.serial)}, and not through ${w.port}`, short: "not the switch's uplink" };
  }
  if (!u.port) return { verdict: "unobserved", why: `${who}'s uplink is not read${u.note ? `: ${u.note}` : ""}`, short: "uplink not read" };
  return u.port === w.port
    ? { verdict: "confirmed", why: `${who} reports ${w.port} as the port it reaches its controller through, and no switch of the site is on the other end` }
    : { verdict: "contradicted", why: `${who}'s WAN port is ${u.port}, not ${w.port}`, short: "not the switch's uplink" };
}
